/**
 * Player identity: an ECDSA P-256 keypair from WebCrypto. The private key is
 * generated non-extractable and kept in IndexedDB (structured-clone of the
 * CryptoKey), so it never leaves the browser and survives reloads.
 *
 *   id = base32(sha256(raw public key))[0..16]
 *
 * Every reliable control message is signed; receivers verify with the
 * sender's public key (learned in its self-certifying `hello`).
 */

const ALG = { name: 'ECDSA', namedCurve: 'P-256' } as const;
const SIG = { name: 'ECDSA', hash: 'SHA-256' } as const;
const B32 = 'abcdefghijklmnopqrstuvwxyz234567';

export interface Identity {
  readonly id: string;
  /** Raw (uncompressed, 65 bytes) public key. */
  readonly pub: Uint8Array;
  sign(data: Uint8Array): Promise<Uint8Array>;
}

function subtle(): SubtleCrypto {
  const c = globalThis.crypto;
  if (!c?.subtle) throw new Error('WebCrypto unavailable (needs a secure context: https or localhost)');
  return c.subtle;
}

export function base32(b: Uint8Array): string {
  let out = '', bits = 0, val = 0;
  for (const byte of b) {
    val = (val << 8) | byte; bits += 8;
    while (bits >= 5) { out += B32[(val >>> (bits - 5)) & 31]; bits -= 5; }
  }
  if (bits > 0) out += B32[(val << (5 - bits)) & 31];
  return out;
}

export async function sha256(data: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await subtle().digest('SHA-256', data as BufferSource));
}

export async function idFromPub(pub: Uint8Array): Promise<string> {
  return base32(await sha256(pub)).slice(0, 16);
}

export function toB64(b: Uint8Array): string {
  let s = '';
  for (const x of b) s += String.fromCharCode(x);
  return btoa(s);
}
export function fromB64(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

const keyCache = new Map<string, Promise<CryptoKey>>();
function importPub(pub: Uint8Array): Promise<CryptoKey> {
  const k = toB64(pub);
  let p = keyCache.get(k);
  if (!p) {
    p = subtle().importKey('raw', pub as BufferSource, ALG, true, ['verify']);
    keyCache.set(k, p);
  }
  return p;
}

export async function verify(pub: Uint8Array, sig: Uint8Array, data: Uint8Array): Promise<boolean> {
  try {
    return await subtle().verify(SIG, await importPub(pub), sig as BufferSource, data as BufferSource);
  } catch {
    return false;
  }
}

async function fromPair(pair: CryptoKeyPair): Promise<Identity> {
  const pub = new Uint8Array(await subtle().exportKey('raw', pair.publicKey));
  const id = await idFromPub(pub);
  const priv = pair.privateKey;
  return {
    id, pub,
    sign: async (data) => new Uint8Array(await subtle().sign(SIG, priv, data as BufferSource)),
  };
}

/** A fresh in-memory identity (tests, or browsers without IndexedDB). */
export async function ephemeralIdentity(): Promise<Identity> {
  return fromPair(await subtle().generateKey(ALG, false, ['sign', 'verify']) as CryptoKeyPair);
}

const DB = 'opencraft-net';
const STORE = 'keys';

function idb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 2);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      if (!db.objectStoreNames.contains('worlds')) db.createObjectStore('worlds');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function openNetDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return null;
  try { return await idb(); } catch { return null; }
}

/**
 * Load the persisted identity or create one. `slot` lets two tabs of the same
 * browser be different players (the session uses a per-tab slot when the
 * default identity is already in use in another tab).
 */
export async function loadOrCreateIdentity(slot = 'default'): Promise<Identity> {
  const db = await openNetDb();
  if (!db) return ephemeralIdentity();
  const get = (): Promise<CryptoKeyPair | undefined> => new Promise((res) => {
    const r = db.transaction(STORE, 'readonly').objectStore(STORE).get(slot);
    r.onsuccess = () => res(r.result as CryptoKeyPair | undefined);
    r.onerror = () => res(undefined);
  });
  let pair = await get();
  if (!pair?.privateKey || !pair.publicKey) {
    pair = await subtle().generateKey(ALG, false, ['sign', 'verify']) as CryptoKeyPair;
    await new Promise<void>((res) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(pair, slot);
      tx.oncomplete = () => res();
      tx.onerror = () => res();
    });
  }
  return fromPair(pair);
}
